'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import {
  Drawer,
  DrawerTitle,
  DrawerContent,
  DrawerTrigger,
  DrawerClose,
} from '@/components/ui/drawer';

import { Phone } from './Phone';
import { Media } from '@/components/Media';
import { CMSLink } from '@/components/Link';
import { Svg } from '@ui/svg';
import { NAV } from '@constants';
import { cn } from '@/utilities/cn';
import type { Menu, Misc } from '@/payload-types';

interface HeaderClientProps {
  menu: Menu;
  misc: Misc;
}

export const HeaderClient: React.FC<HeaderClientProps> = ({ menu, misc }) => {
  const pathname = usePathname();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { header } = menu;
  const { logo } = misc.settings;
  const { phones, emails, address } = misc.refs;

  useEffect(() => {
    const onPageScroll = () => {
      if (window.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', onPageScroll);
    return () => window.removeEventListener('scroll', onPageScroll);
  }, []);

  useEffect(() => {
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  }, [pathname]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 z-100 w-full',
        scrolled && 'bg-white/90 backdrop-blur-sm',
      )}
    >
      <div className="container">
        <div className="flex h-14 items-center gap-2 lg:h-12">
          <Link className="w-[166px] lg:w-[108px]" href={NAV.home}>
            <Media resource={logo} priority loading="eager" />
          </Link>

          <nav className="mx-auto flex gap-6 xl:hidden">
            {header?.navItems?.map(({ link }, id) => (
              <CMSLink className="h7 link" key={id} {...link} />
            ))}
          </nav>

          <div className="flex items-center gap-2 xl:ml-auto">
            <div className="flex-center h7 h-10 gap-1 rounded-full border border-gray-700 p-[3px] pr-4 text-[#3E6ED4] lg:hidden">
              <div className="flex-center aspect-square w-8 rounded-full bg-gray-700">
                <Svg id="location" width="14" height="18" />
              </div>
              {address?.city}
            </div>

            <Phone className="sm:text-[11px]" phone={phones?.[0].phone} />

            {/* Drawer */}
            <Drawer
              open={isDrawerOpen}
              onOpenChange={() => setIsDrawerOpen((p) => !p)}
            >
              <DrawerTrigger className="flex-center aspect-square w-8 rounded-full bg-gray-700 text-[#3E6ED4]">
                <Svg id="burger" width="12" height="7" />
              </DrawerTrigger>

              <DrawerContent
                className="top-0! right-0! bottom-0! w-[390px] rounded-none! bg-cover"
                overlayClassName="bg-transparent"
              >
                <div className="flex flex-col gap-12">
                  <DrawerClose />

                  <DrawerTitle>
                    <Link className="w-[170px]" href={NAV.home}>
                      <Media resource={logo} priority loading="eager" />
                    </Link>
                  </DrawerTitle>

                  <div className="text-h6 flex flex-col gap-5">
                    {header?.navItems?.map(({ link }, id) => (
                      <CMSLink className="link" key={id} {...link} />
                    ))}
                  </div>

                  <Phone phone={phones?.[0].phone} />

                  {emails && (
                    <div className="flex flex-col gap-3">
                      {emails?.map((i) => (
                        <a
                          className="link"
                          href={`mailto:${i.email}`}
                          key={i.id}
                        >
                          {i.email}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      </div>
    </header>
  );
};
